// Version check utility to detect when the app has been updated
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const VERSION_KEY = 'app-version';

let versionCheckInterval: NodeJS.Timeout | null = null;

export function generateVersion(): string {
  // In production, this should be replaced with a build-time generated version
  return new Date().toISOString();
}

export function getStoredVersion(): string | null {
  return localStorage.getItem(VERSION_KEY);
}

export function setStoredVersion(version: string): void {
  localStorage.setItem(VERSION_KEY, version);
}

export async function checkForUpdates(): Promise<boolean> {
  try {
    // Try to fetch a version file or the index.html to check if it has changed
    const response = await fetch('/index.html', {
      method: 'HEAD',
      cache: 'no-cache',
    });
    
    const etag = response.headers.get('etag');
    const lastModified = response.headers.get('last-modified');
    
    const currentVersion = etag || lastModified || '';
    const storedVersion = getStoredVersion();
    
    if (storedVersion && currentVersion && storedVersion !== currentVersion) {
      console.log('New version detected:', { current: currentVersion, stored: storedVersion });
      // Note: Don't clear stale data here - only clear when user clicks "Refresh Now"
      // Clearing here would break the current session (e.g., remove X-Team-Context)
      return true;
    }
    
    // Store the current version
    if (currentVersion) {
      setStoredVersion(currentVersion);
    }
    
    return false;
  } catch (error) {
    console.error('Failed to check for updates:', error);
    return false;
  }
}

export function startVersionCheck(onUpdateDetected: () => void): void {
  // Clear any existing interval
  stopVersionCheck();
  
  // Check immediately
  checkForUpdates().then(hasUpdate => {
    if (hasUpdate) {
      onUpdateDetected();
    }
  });
  
  // Check periodically
  versionCheckInterval = setInterval(async () => {
    const hasUpdate = await checkForUpdates();
    if (hasUpdate) {
      onUpdateDetected();
    }
  }, VERSION_CHECK_INTERVAL);
}

export function stopVersionCheck(): void {
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval);
    versionCheckInterval = null;
  }
}

// Clear stale data from localStorage that might cause issues after version updates
export function clearStaleLocalStorageData(): void {
  try {
    // Clear teams data from auth-storage to force fresh fetch
    // This prevents errors from stale team data structure mismatches
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      try {
        const authData = JSON.parse(authStorage);
        if (authData.state) {
          // Clear teams to force a fresh fetch on next page load
          authData.state.teams = [];
          authData.state.currentTeam = null;
          authData.state.currentTeamId = null;
          localStorage.setItem('auth-storage', JSON.stringify(authData));
          console.log('Cleared stale teams data from localStorage');
        }
      } catch (parseError) {
        console.error('Failed to parse auth-storage:', parseError);
        // If we can't parse it, remove it entirely (user will need to re-login)
        localStorage.removeItem('auth-storage');
      }
    }
  } catch (error) {
    console.error('Failed to clear stale localStorage data:', error);
  }
}

// Utility to clear all caches and reload
export async function clearCachesAndReload(): Promise<void> {
  try {
    // Clear service worker caches if available
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // Unregister service workers if any
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }

    // Clear local storage version
    localStorage.removeItem(VERSION_KEY);

    // Clear stale app data that might cause issues with new version
    clearStaleLocalStorageData();

    // Force hard reload
    window.location.reload();
  } catch (error) {
    console.error('Failed to clear caches:', error);
    // Fallback to simple reload
    window.location.reload();
  }
}